import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FinetuneDataset } from "../../model_gen/finetuning";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2Icon, MoreHorizontalIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { usePermissions } from "../../PermissionsProvider";
import { useTagConfig } from "../../hooks/useTagConfig";
import { DatasetService } from "../../services/DatasetService";

import moment from "moment";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";

type DatasetStatus = string | undefined;

function getStatusDisplay(status: DatasetStatus): {
  label: string;
  className: string;
  isActive: boolean;
} | null {
  switch (status) {
    case "GENERATION_PENDING":
      return {
        label: "Queued",
        className: "border-blue-500/30 text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-400",
        isActive: true,
      };
    case "GENERATING":
      return {
        label: "Generating",
        className: "border-maitai-green/30 text-maitai-green bg-maitai-green/10",
        isActive: true,
      };
    case "GENERATION_PAUSING":
      return {
        label: "Pausing…",
        className: "border-amber-500/30 text-amber-600 bg-amber-50 dark:bg-amber-950 dark:text-amber-400",
        isActive: true,
      };
    case "GENERATION_PAUSED":
      return {
        label: "Paused",
        className: "border-amber-500/30 text-amber-600 bg-amber-50 dark:bg-amber-950 dark:text-amber-400",
        isActive: false,
      };
    case "GENERATION_CANCELLED":
      return {
        label: "Cancelled",
        className: "border-muted-foreground/30 text-muted-foreground bg-muted/20",
        isActive: false,
      };
    case "GENERATION_ERROR":
      return {
        label: "Error",
        className: "border-red-500/30 text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400",
        isActive: false,
      };
    case "IMPORT_PENDING":
    case "IMPORTING":
      return {
        label: status === "IMPORT_PENDING" ? "Import Pending" : "Importing",
        className: "border-blue-500/30 text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-400",
        isActive: true,
      };
    default:
      return null;
  }
}

interface DatasetsTableProps {
  datasets: FinetuneDataset[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onDeleteDataset?: (dataset: FinetuneDataset) => void;
  onDatasetUpdated?: (dataset: FinetuneDataset) => void;
  emptyTitle?: string;
  emptyDescription?: string;
}

const DatasetsTable: React.FC<DatasetsTableProps> = ({
  datasets,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onDeleteDataset,
  onDatasetUpdated,
  emptyTitle = "No datasets found",
  emptyDescription = "Create a new dataset to get started with model fine-tuning.",
}) => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const { getTagDisplayConfig } = useTagConfig(apiKey);
  const [deletingDatasetId, setDeletingDatasetId] = useState<number | null>(
    null
  );
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<FinetuneDataset>[] = [
    {
      key: "name",
      header: "Name",
      width: "w-[18%]",
      sortable: false,
      render: (dataset) => {
        const name = dataset.name || "";
        const truncated = name.length > 25 ? name.substring(0, 25) + "..." : name;
        const needsTooltip = name.length > 25;
        
        return (
          <div className="relative group">
            <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
              {truncated}
            </div>
            {needsTooltip && (
              <div className="absolute left-0 bottom-full mb-2 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                <div className="bg-popover border border-border rounded-md shadow-lg px-2 py-1 max-w-xs">
                  <span className="text-sm text-foreground whitespace-normal break-words">{name}</span>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "meta" as keyof FinetuneDataset,
      header: "Tags",
      width: "w-[15%]",
      sortable: false,
      render: (dataset) => {
        const tags: string[] = (dataset as any)?.meta?.tags || [];
        const hasTestRequests = (dataset as any)?.meta?.used_in_test === true || 
                                (dataset as any)?.meta?.contains_test_requests === true;
        
        // Build all tags including test requests indicator
        const allTags: { key: string; label: string; className: string }[] = [];
        
        if (hasTestRequests) {
          allTags.push({
            key: "_test_requests",
            label: "Test Requests",
            className: "border-violet-500/30 text-violet-700 bg-violet-50 dark:bg-violet-950 dark:text-violet-300",
          });
        }
        
        tags.forEach((tag: string) => {
          const config = getTagDisplayConfig(tag);
          allTags.push({
            key: tag,
            label: config.label,
            className: config.className,
          });
        });
        
        if (allTags.length === 0) {
          return <span className="text-muted-foreground text-xs">-</span>;
        }
        
        // Show up to 2 tags inline
        const visibleTags = allTags.slice(0, 2);
        const hiddenTags = allTags.slice(2);
        const hiddenCount = hiddenTags.length;
        
        return (
          <div className="flex items-center gap-1 flex-wrap">
            {visibleTags.map((tag) => (
              <Badge
                key={tag.key}
                variant="outline"
                className={cn("text-xs whitespace-nowrap", tag.className)}
              >
                {tag.label}
              </Badge>
            ))}
            {hiddenCount > 0 && (
              <div className="relative group/more">
                <span className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-help">
                  +{hiddenCount}
                </span>
                <div className="absolute left-0 bottom-full mb-2 z-50 invisible group-hover/more:visible opacity-0 group-hover/more:opacity-100 transition-opacity duration-150">
                  <div className="bg-popover border border-border rounded-md shadow-lg p-2 flex flex-col gap-1.5">
                    {hiddenTags.map((tag) => (
                      <Badge
                        key={tag.key}
                        variant="outline"
                        className={cn("text-xs whitespace-nowrap", tag.className)}
                      >
                        {tag.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "meta" as keyof FinetuneDataset,
      header: "Status",
      width: "w-[10%]",
      sortable: false,
      render: (dataset) => {
        const status = (dataset as any)?.meta?.status as DatasetStatus;
        const display = getStatusDisplay(status);
        if (!display) {
          return <span className="text-muted-foreground text-xs">Ready</span>;
        }
        return (
          <div className="flex items-center gap-1.5">
            {display.isActive && (
              <Loader2Icon className="w-3 h-3 animate-spin text-maitai-green shrink-0" />
            )}
            <Badge
              variant="outline"
              className={cn("text-xs whitespace-nowrap", display.className)}
            >
              {display.label}
            </Badge>
          </div>
        );
      },
    },
    {
      key: "major_version",
      header: "Version",
      width: "w-[8%]",
      sortable: false,
      render: (dataset) => (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          v{dataset.major_version || 1}.{dataset.minor_version || 0}
        </span>
      ),
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[15%]",
      sortable: false,
      render: (dataset) =>
        dataset.date_created ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(dataset.date_created)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(dataset.date_created)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        ),
    },
    {
      key: "intent_group_name",
      header: "Source",
      width: "w-[20%]",
      sortable: false,
      render: (dataset) => (
        <div className="text-sm font-gc-reg text-foreground">
          {dataset.intent_group_name ||
            (dataset.application_name && dataset.intent_name
              ? `${dataset.application_name} / ${dataset.intent_name}`
              : "-")}
        </div>
      ),
    },
    {
      key: "request_count",
      header: "Requests",
      width: "w-[10%]",
      sortable: false,
      render: (dataset) => (
        <span className="text-sm font-gc-med text-foreground">
          {dataset.request_count ? dataset.request_count.toLocaleString() : "0"}
        </span>
      ),
    },
    {
      key: "id",
      header: "",
      width: "w-[10%]",
      sortable: false,
      render: (dataset) => {
        const status = (dataset as any)?.meta?.status as DatasetStatus;
        const isGenerating = status === "GENERATING" || status === "GENERATION_PENDING";
        const isPausing = status === "GENERATION_PAUSING";
        const isPaused = status === "GENERATION_PAUSED";
        const isBusy = actionInProgress === dataset.id;

        const handleAction = (
          action: "pause" | "stop" | "resume" | "delete",
          menuEl: HTMLElement,
        ) => {
          menuEl.remove();
          if (isBusy) return;
          setActionInProgress(dataset.id);

          if (action === "delete") {
            setDeletingDatasetId(dataset.id);
            onDeleteDataset?.(dataset);
            setTimeout(() => {
              setDeletingDatasetId(null);
              setActionInProgress(null);
            }, 1000);
            return;
          }

          const serviceFn =
            action === "pause"
              ? DatasetService.pauseGeneration
              : action === "resume"
              ? DatasetService.resumeGeneration
              : DatasetService.cancelGeneration;

          serviceFn(dataset.id, undefined, apiKey).subscribe(
            () => {
              DatasetService.getDataset(dataset.id, undefined, apiKey).subscribe(
                (updated: FinetuneDataset) => {
                  onDatasetUpdated?.(updated);
                  setActionInProgress(null);
                },
                () => setActionInProgress(null),
              );
            },
            () => setActionInProgress(null),
          );
        };

        return (
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                const menu = document.createElement("div");
                menu.className =
                  "absolute right-0 top-8 z-50 min-w-48 bg-popover border border-border rounded-md shadow-lg py-1";

                const addItem = (label: string, action: "pause" | "stop" | "resume" | "delete", className: string) => {
                  const btn = document.createElement("button");
                  btn.className = `flex items-center w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${className}`;
                  btn.textContent = label;
                  btn.onclick = (evt) => {
                    evt.stopPropagation();
                    handleAction(action, menu);
                  };
                  menu.appendChild(btn);
                };

                if (isGenerating) {
                  addItem("Pause", "pause", "text-amber-600");
                  addItem("Stop", "stop", "text-red-600");
                } else if (isPausing) {
                  addItem("Stop", "stop", "text-red-600");
                } else if (isPaused) {
                  addItem("Resume", "resume", "text-maitai-green");
                  addItem("Stop", "stop", "text-red-600");
                }

                addItem(
                  deletingDatasetId === dataset.id ? "Deleting..." : "Delete",
                  "delete",
                  deletingDatasetId === dataset.id
                    ? "text-muted-foreground cursor-not-allowed"
                    : "text-red-600",
                );

                const container = e.currentTarget.parentElement;
                if (container) {
                  container.appendChild(menu);
                  const handleClickOutside = (evt: Event) => {
                    if (!menu.contains(evt.target as Node)) {
                      menu.remove();
                      document.removeEventListener("click", handleClickOutside);
                    }
                  };
                  setTimeout(
                    () => document.addEventListener("click", handleClickOutside),
                    0,
                  );
                }
              }}
            >
              <MoreHorizontalIcon className="w-4 h-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const handleRowClick = (dataset: FinetuneDataset) => {
    navigate(`/forge/datasets/${dataset.id}`);
  };

  return (
    <PaginatedTable
      data={datasets}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={handleRowClick}
      rowClassName="hover:bg-accent/20 transition-colors"
      showPagination={true}
    />
  );
};

export default DatasetsTable;
